import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { 
  RefreshCw, 
  ArrowRight, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Eye,
  XCircle,
  Filter,
  TrendingUp,
  Search,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_COLORS = {
  registered: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle },
  published: { bg: "bg-amber-100", text: "text-amber-700", icon: Eye },
  examination: { bg: "bg-blue-100", text: "text-blue-700", icon: Clock },
  opposed: { bg: "bg-purple-100", text: "text-purple-700", icon: AlertTriangle },
  dead: { bg: "bg-slate-100", text: "text-slate-500", icon: XCircle }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_COLORS[status] || STATUS_COLORS.examination;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {status?.toUpperCase()}
    </span>
  );
};

export const StatusChanges = () => {
  const [summary, setSummary] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, watched
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, changesRes] = await Promise.all([
        axios.get(`${API}/status-changes/summary`),
        axios.get(`${API}/status-changes`, {
          params: {
            limit: 50,
            watched_only: filter === "watched",
            status: statusFilter || undefined,
            search: searchQuery || undefined
          }
        })
      ]);
      
      setSummary(summaryRes.data);
      setChanges(changesRes.data.changes);
    } catch (error) {
      console.error("Error fetching status changes:", error);
      toast.error("Failed to load status changes");
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading && !summary) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Status Changes
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Track status movements of UK IPO trademark applications
          </p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="refresh-status-changes-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="swiss-card p-4"
            data-testid="total-changes-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_changes}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Changes (30d)</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="swiss-card p-4"
            data-testid="watched-changes-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.watched_changes}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Watched Changes</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="swiss-card p-4"
            data-testid="registered-changes-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {summary.changes_by_status?.registered || 0}
                </p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Now Registered</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="swiss-card p-4"
            data-testid="opposed-changes-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {summary.changes_by_status?.opposed || 0}
                </p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Now Opposed</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search by trademark name, number, or applicant..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 pr-10"
            data-testid="status-changes-search-input"
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="clear-search-btn"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Filter:</span>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
              data-testid="filter-all-btn"
            >
              All Changes
            </Button>
            <Button
              variant={filter === "watched" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("watched")}
              data-testid="filter-watched-btn"
            >
              Watched Only
            </Button>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border rounded-md px-3 py-1.5 bg-white"
            data-testid="status-filter-select"
          >
            <option value="">All Statuses</option>
            <option value="registered">Registered</option>
            <option value="published">Published</option>
            <option value="examination">Examination</option>
            <option value="opposed">Opposed</option>
            <option value="dead">Dead</option>
          </select>
        </div>
      </div>

      {/* Active Search Indicator */}
      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
          <Search className="w-4 h-4" />
          <span>Showing results for "<strong>{searchQuery}</strong>"</span>
          <button
            onClick={clearSearch}
            className="ml-auto text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Changes List */}
      <div className="swiss-card overflow-hidden" data-testid="status-changes-list">
        {changes.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              {searchQuery ? <Search className="w-8 h-8 text-slate-400" /> : <TrendingUp className="w-8 h-8 text-slate-400" />}
            </div>
            <h3 className="font-medium text-slate-900 mb-2">
              {searchQuery ? "No Matching Results" : "No Status Changes Yet"}
            </h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              {searchQuery 
                ? `No status changes found matching "${searchQuery}". Try a different search term.`
                : "Status changes will appear here when you import updated UK IPO data or manually update application statuses."
              }
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearSearch}
                className="mt-4"
              >
                Clear Search
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {changes.map((change, index) => (
              <motion.div
                key={change.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 hover:bg-slate-50 transition-colors ${
                  change.is_watched ? "border-l-4 border-amber-400" : ""
                }`}
                data-testid={`status-change-${change.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-slate-900 truncate">
                        {change.trademark_name}
                      </h3>
                      {change.is_watched && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Eye className="w-3 h-3" />
                          Watched
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-2">
                      {change.application_number} • {change.applicant}
                    </p>
                    
                    {/* Status Change Visual */}
                    <div className="flex items-center gap-2">
                      <StatusBadge status={change.old_status} />
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <StatusBadge status={change.new_status} />
                    </div>
                    
                    {change.related_alert_count > 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        Affects {change.related_alert_count} alert{change.related_alert_count !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                  
                  <div className="text-right text-xs text-slate-400 whitespace-nowrap">
                    <p>{formatDate(change.changed_at)}</p>
                    <p className="mt-1 capitalize">{change.source}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">How Status Tracking Works</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Status changes are automatically detected when you import new UK IPO data</li>
          <li>• Manual status updates also create tracking records</li>
          <li>• "Watched" trademarks are those that appear in your alerts</li>
          <li>• Email notifications are sent when watched trademarks change status</li>
        </ul>
      </div>
    </div>
  );
};
